import { NumberInput, Select, Switch, Text, Textarea, TextInput } from '@mantine/core';
import { useTranslation } from 'react-i18next';

import {
  computeCurrentValue,
  draftSourceLabel,
  formatEuros,
} from 'src/Components/Inventory/inventoryLabels';
import { InventoryValueSource } from 'src/Services/API/generated';

import styles from './InventoryItemFields.module.scss';
import { Props } from './InventoryItemFields.types';

const CURRENT_YEAR = new Date().getFullYear();

const toNumber = (value: string | number): number | null =>
  value === '' || value === null || value === undefined ? null : Number(value);

/** The object card: what every member fills, then what only managers see. */
const InventoryItemFields = ({
  values,
  onChange,
  roomOptions,
  valuesVisible,
  rule,
  sources,
  nameError,
}: Props) => {
  const { t } = useTranslation();
  const sourceHint = (source?: string) => {
    const label = draftSourceLabel(t, source);
    return label ? <span className={styles.source}>{label}</span> : null;
  };
  const currentValue = computeCurrentValue(values, rule);

  return (
    <div className={styles.container}>
      <TextInput
        size="md"
        required
        label={
          <>
            {t('inventory.fields.name', 'Objet')} {sourceHint(sources?.name)}
          </>
        }
        placeholder={t('inventory.fields.namePlaceholder', 'Ex. Canapé 3 places')}
        value={values.name}
        error={nameError}
        onChange={event => onChange({ name: event.currentTarget.value })}
      />
      <Select
        size="md"
        label={t('inventory.fields.room', 'Pièce')}
        placeholder={t('inventory.fields.roomPlaceholder', 'Choisir une pièce')}
        data={roomOptions}
        value={values.roomId}
        clearable
        searchable
        onChange={roomId => onChange({ roomId })}
      />
      <TextInput
        size="md"
        label={
          <>
            {t('inventory.fields.brandAndModel', 'Marque et modèle')}{' '}
            {sourceHint(sources?.brandAndModel)}
          </>
        }
        placeholder={t('inventory.fields.brandPlaceholder', 'Ex. Ikea Kivik')}
        value={values.brandAndModel}
        onChange={event => onChange({ brandAndModel: event.currentTarget.value })}
      />
      <Textarea
        size="md"
        autosize
        minRows={2}
        label={
          <>
            {t('inventory.fields.condition', 'État')} {sourceHint(sources?.condition)}
          </>
        }
        placeholder={t(
          'inventory.fields.conditionPlaceholder',
          'Ex. Usure normale, un pied qui bouge'
        )}
        value={values.condition}
        onChange={event => onChange({ condition: event.currentTarget.value })}
      />
      <div className={styles.row}>
        <NumberInput
          size="md"
          label={t('inventory.fields.quantity', 'Quantité')}
          min={1}
          max={10000}
          allowDecimal={false}
          value={values.quantity}
          onChange={value => onChange({ quantity: Math.max(1, toNumber(value) ?? 1) })}
        />
        <NumberInput
          size="md"
          label={
            <>
              {t('inventory.fields.purchaseYear', "Année d'achat")} {sourceHint(sources?.purchase)}
            </>
          }
          placeholder={t('inventory.fields.purchaseYearPlaceholder', 'Ex. 2005')}
          min={1800}
          max={CURRENT_YEAR}
          allowDecimal={false}
          thousandSeparator=""
          value={values.purchaseYear ?? ''}
          onChange={value => onChange({ purchaseYear: toNumber(value) })}
        />
      </div>

      {valuesVisible && (
        <div className={styles.values}>
          <div className={styles.row}>
            <NumberInput
              size="md"
              label={t('inventory.fields.purchasePrice', "Prix d'achat")}
              placeholder={t('inventory.fields.amountPlaceholder', 'Ex. 600')}
              suffix=" €"
              min={0}
              thousandSeparator=" "
              value={values.purchasePrice ?? ''}
              onChange={value => onChange({ purchasePrice: toNumber(value) })}
            />
            <NumberInput
              size="md"
              label={t('inventory.fields.replacementValue', 'Valeur à neuf')}
              placeholder={t('inventory.fields.replacementPlaceholder', 'Prix neuf aujourd’hui')}
              suffix=" €"
              min={0}
              thousandSeparator=" "
              value={values.replacementValue ?? ''}
              onChange={value =>
                onChange({
                  replacementValue: toNumber(value),
                  replacementValueSource:
                    toNumber(value) === null ? null : InventoryValueSource.Manual,
                  replacementValueSourceLabel: null,
                  replacementValueSourceUrl: null,
                })
              }
            />
          </div>
          {values.replacementValueSource === InventoryValueSource.Lucien &&
            values.replacementValueSourceUrl && (
              <Text size="sm" c="dimmed">
                {t('inventory.fields.lucienSource', 'Prix trouvé par Lucien :')}{' '}
                <a href={values.replacementValueSourceUrl} target="_blank" rel="noreferrer">
                  {values.replacementValueSourceLabel ?? values.replacementValueSourceUrl}
                </a>
              </Text>
            )}
          <Switch
            size="md"
            label={t('inventory.fields.depreciation', 'Cet objet perd de la valeur avec le temps')}
            checked={values.depreciationEnabled}
            onChange={event => onChange({ depreciationEnabled: event.currentTarget.checked })}
          />
          <div className={styles.current}>
            <span className={styles.currentLabel}>
              {t('inventory.fields.currentValue', 'Vaut aujourd’hui, à peu près')}
            </span>
            <span className={styles.currentValue}>
              {currentValue !== null
                ? formatEuros(currentValue)
                : t('inventory.toComplete', 'à compléter')}
            </span>
            <Text size="sm" c="dimmed">
              {values.depreciationEnabled
                ? t(
                    'inventory.fields.ruleHint',
                    'Valeur à neuf moins {{rate}} % par an depuis l’achat, au plus {{max}} %. Estimation indicative : en cas de sinistre, l’expert de l’assurance décide.',
                    { rate: rule.depreciationRatePercent, max: rule.depreciationMaxPercent }
                  )
                : t(
                    'inventory.fields.noWearHint',
                    'Sans usure : la valeur à neuf est gardée. Estimation indicative : en cas de sinistre, l’expert de l’assurance décide.'
                  )}
            </Text>
          </div>
        </div>
      )}

      <Textarea
        size="md"
        autosize
        minRows={2}
        label={t('inventory.fields.notes', 'Notes')}
        placeholder={t('inventory.fields.notesPlaceholder', 'Ex. Numéro de série, garantie…')}
        value={values.notes}
        onChange={event => onChange({ notes: event.currentTarget.value })}
      />
    </div>
  );
};

export default InventoryItemFields;
